<script lang="ts">
  import BottomSheet from './BottomSheet.svelte';
  import type {
    EffectKey,
    RouletteSettings,
    SpinSoundKey,
    WinSoundKey,
    TextOrientation,
    TextSizeMode,
    TextAlign,
    MarkerPosition,
    MarkerStyle,
    Direction,
  } from '../lib/types';
  import { defaultSettings, EFFECT_KEYS } from '../lib/defaults';
  import { soundManager } from '../lib/soundManager';
  import { appState, previewEffect, transientState } from '../lib/state.svelte.ts';
  import {
    getEffectivePhaseTimes,
    PEAK_SPEED_MIN_RPS,
    PEAK_SPEED_MAX_RPS,
    SPIN_DURATION_RANDOM_MAX_RATIO,
  } from '../lib/spinEngine';
  import type { SpinPhaseOverrides, SpinCurve, SpinPresetProfile } from '../lib/types';

  type OverrideField = keyof SpinPhaseOverrides;
  type NumericField = 'pushEndMs' | 'cruiseEndMs' | 'decelEndMs' |
                      'pushEndRps' | 'cruiseEndRps' | 'decelEndRps';
  type CurveField = 'pushCurve' | 'cruiseCurve' | 'decelCurve' | 'landingCurve';

  function getOverride<F extends OverrideField>(field: F): SpinPhaseOverrides[F] | undefined {
    const ov = local.spinPhaseOverrides;
    return ov ? ov[field] : undefined;
  }
  function isOverridden(field: OverrideField): boolean {
    return getOverride(field) !== undefined;
  }
  /** 時間境界の上書き設定。下流 (cruise/decel) が新しい値より小さくなる場合は
   *  自動的に押し出す (ripple cascade) → 上流調整で下流が無視されない。 */
  function setNumericOverride(field: NumericField, value: number): void {
    const ov: SpinPhaseOverrides = { ...(local.spinPhaseOverrides ?? {}) };
    ov[field] = value;

    if (field === 'pushEndMs' || field === 'cruiseEndMs' || field === 'decelEndMs') {
      const totalMs = local.spinDurationMs ?? 5000;
      const minLen = 100;
      // 現在の下流境界 (override 優先、無ければ effTimes から取得)
      const curCruise = ov.cruiseEndMs ?? effTimes.cruiseEndMs;
      const curDecel = ov.decelEndMs ?? effTimes.decelEndMs;
      if (field === 'pushEndMs') {
        if (curCruise <= value + minLen) {
          ov.cruiseEndMs = Math.min(value + minLen, totalMs - 2 * minLen);
        }
        const newCruise = ov.cruiseEndMs ?? curCruise;
        if (curDecel <= newCruise + minLen) {
          ov.decelEndMs = Math.min(newCruise + minLen, totalMs - minLen);
        }
      } else if (field === 'cruiseEndMs') {
        if (curDecel <= value + minLen) {
          ov.decelEndMs = Math.min(value + minLen, totalMs - minLen);
        }
      }
    }
    local.spinPhaseOverrides = ov;
  }
  function setCurveOverride(field: CurveField, value: SpinCurve): void {
    const ov: SpinPhaseOverrides = { ...(local.spinPhaseOverrides ?? {}) };
    ov[field] = value;
    local.spinPhaseOverrides = ov;
  }
  function clearOverride(field: OverrideField): void {
    if (!local.spinPhaseOverrides) return;
    const ov: SpinPhaseOverrides = { ...local.spinPhaseOverrides };
    delete ov[field];
    const isEmpty = Object.keys(ov).length === 0;
    local.spinPhaseOverrides = isEmpty ? null : ov;
  }

  const CURVES: { key: SpinCurve; label: string }[] = [
    { key: 'linear',    label: '線形' },
    { key: 'easeOut',   label: '減速' },
    { key: 'easeIn',    label: '加速' },
    { key: 'easeInOut', label: 'S字' },
  ];

  const PRESET_PROFILES: { key: SpinPresetProfile; label: string; desc: string }[] = [
    { key: 'z', label: 'バランス型', desc: '本命。短時間は初速早く、長時間はクレッシェンド (default)' },
    { key: 'y', label: '緩急強調',   desc: '投擲 → 一気に止まりかけ → 長い余韻 (コントラスト最大)' },
    { key: 'x', label: 'AIお勧め',   desc: '滑らかなクレッシェンド + 滑らかな減速 (映画的演出)' },
  ];

  interface Props {
    open: boolean;
    settings: RouletteSettings;
    onsave: (s: RouletteSettings) => void;
    onclose: () => void;
  }

  let { open, settings, onsave, onclose }: Props = $props();

  // 編集中のローカルコピー (open になるたびに settings から複製)
  let local = $state<RouletteSettings>({} as RouletteSettings);

  $effect(() => {
    if (open) {
      local = {
        ...settings,
        // SpinEffectSettings はネストが深いので deepcopy (range も含めて)
        spinEffects: {
          ...settings.spinEffects,
          effects: Object.fromEntries(
            EFFECT_KEYS.map((k) => {
              const c = settings.spinEffects.effects[k];
              return [k, {
                ...c,
                probabilityRange: { ...c.probabilityRange },
                timingRange: { ...c.timingRange },
              }];
            })
          ) as Record<EffectKey, typeof settings.spinEffects.effects[EffectKey]>,
        },
      };
    }
  });

  // 効果値 (実行時のクランプ後): UI 表示用 — spinEngine と完全一致
  let effTimes = $derived(getEffectivePhaseTimes(
    local.spinDurationMs ?? 5000,
    local.spinPhaseOverrides,
    local.spinPresetProfile ?? 'z',
  ));
  // 各 phase 境界秒 (派生)
  let pushEndSec   = $derived(effTimes.pushEndMs / 1000);
  let cruiseEndSec = $derived(effTimes.cruiseEndMs / 1000);
  let decelEndSec  = $derived(effTimes.decelEndMs / 1000);
  let totalSec     = $derived(effTimes.totalMs / 1000);

  // ─── スピン詳細の Save/Cancel/Reset 用 snapshot 管理 ───
  // open 時 / 「保存」時に snapshot を更新。「キャンセル」で snapshot に戻す。
  let detailSnapshot = $state<{
    overrides: SpinPhaseOverrides | null;
    randomRatio: number;
  }>({ overrides: null, randomRatio: 0 });

  $effect(() => {
    if (open) {
      detailSnapshot = {
        overrides: settings.spinPhaseOverrides ? { ...settings.spinPhaseOverrides } : null,
        randomRatio: settings.spinDurationRandomRatio ?? 0,
      };
    }
  });

  let detailDirty = $derived(
    JSON.stringify(local.spinPhaseOverrides ?? null) !== JSON.stringify(detailSnapshot.overrides) ||
    (local.spinDurationRandomRatio ?? 0) !== detailSnapshot.randomRatio,
  );

  /** スピン詳細だけ即時保存 (シートを閉じずに親 settings へ反映)。 */
  function saveDetailNow(): void {
    onsave({ ...local });
    detailSnapshot = {
      overrides: local.spinPhaseOverrides ? { ...local.spinPhaseOverrides } : null,
      randomRatio: local.spinDurationRandomRatio ?? 0,
    };
  }
  /** スピン詳細をスナップショット (前回保存時) に戻す。 */
  function cancelDetail(): void {
    local.spinPhaseOverrides = detailSnapshot.overrides ? { ...detailSnapshot.overrides } : null;
    local.spinDurationRandomRatio = detailSnapshot.randomRatio;
  }
  /** スピン詳細を全プリセット既定値に戻し、即保存。 */
  function resetDetailToPreset(): void {
    local.spinPhaseOverrides = null;
    local.spinDurationRandomRatio = 0;
    saveDetailNow();
  }

  /** 数値入力からの値受付 (min/max クランプ + ripple) */
  function setNumericFromInput(field: NumericField, raw: string): void {
    const v = parseFloat(raw);
    if (!Number.isFinite(v)) return;
    setNumericOverride(field, v);
  }
  /** 時間 (秒) → ms 変換して設定 */
  function setTimeSecOverride(field: 'pushEndMs' | 'cruiseEndMs' | 'decelEndMs', sec: number): void {
    setNumericOverride(field, sec * 1000);
  }

  const SPIN_SOUNDS: { key: SpinSoundKey; label: string }[] = [
    { key: 'click', label: 'クリック' },
    { key: 'snap', label: 'スナップ' },
    { key: 'drum', label: 'ドラム' },
    { key: 'coin', label: 'コイン' },
    { key: 'soft', label: 'ソフト' },
    { key: 'mute', label: 'なし' },
  ];

  const WIN_SOUNDS: { key: WinSoundKey; label: string }[] = [
    { key: 'fanfare', label: 'ファンファーレ' },
    { key: 'bell', label: 'ベル' },
    { key: 'casino', label: 'カジノ' },
    { key: 'chime', label: 'チャイム' },
    { key: 'victory', label: 'ビクトリー' },
    { key: 'mute', label: 'なし' },
  ];

  const TEXT_ORIENTATIONS: { key: TextOrientation; label: string }[] = [
    { key: 'radial', label: '放射' },
    { key: 'tangent', label: '接線' },
    { key: 'horizontal', label: '水平' },
  ];

  const TEXT_SIZES: { key: TextSizeMode; label: string }[] = [
    { key: 'auto', label: '自動' },
    { key: 'fixed', label: '固定' },
  ];

  const TEXT_ALIGNS: { key: TextAlign; label: string }[] = [
    { key: 'outer', label: '外側' },
    { key: 'center', label: '中央' },
  ];

  const MARKERS: { key: MarkerPosition; label: string }[] = [
    { key: 'top', label: '上' },
    { key: 'right', label: '右' },
    { key: 'bottom', label: '下' },
    { key: 'left', label: '左' },
  ];

  const MARKER_STYLES: { key: MarkerStyle; label: string }[] = [
    { key: 'inside', label: '内側' },
    { key: 'overlap', label: '少し被る' },
    { key: 'outside', label: '外側' },
  ];

  const DIRECTIONS: { key: Direction; label: string }[] = [
    { key: 'cw', label: '時計回り' },
    { key: 'ccw', label: '反時計回り' },
  ];

  const EFFECT_LABELS: Record<EffectKey, string> = {
    soundConfirm:    '確定音 (target当選)',
    soundExpect:     '期待度アップ音',
    soundNgConfirm:  'NG確定音 (avoid当選)',
    miniCharTarget:  'ミニドラゴン (target確定)',
    miniCharExpect:  'ミニウサギ (期待度アップ)',
    miniCharNg:      'ミニゴースト (avoid確定)',
    cutInTarget:     'ドラゴンカットイン',
    cutInExpect:     'ウサギカットイン',
    cutInNg:         'ゴーストカットイン',
    flashConfirm:    '画面フラッシュ',
    wheelGlow:       'ホイール虹色グロー',
    textChance:      'CHANCE!テキスト',
  };

  /** 演出グループ表示 (説明テキストはユーザー要望で削除) */
  const EFFECT_GROUPS: { label: string; keys: EffectKey[] }[] = [
    { label: '音系', keys: ['soundConfirm', 'soundExpect', 'soundNgConfirm'] },
    { label: 'ミニキャラ系', keys: ['miniCharTarget', 'miniCharExpect', 'miniCharNg'] },
    { label: 'カットイン系', keys: ['cutInTarget', 'cutInExpect', 'cutInNg'] },
    { label: 'その他', keys: ['flashConfirm', 'wheelGlow', 'textChance'] },
  ];

  function resetEffectsToDefault(): void {
    local.spinEffects = {
      ...defaultSettings().spinEffects,
      // マスター ON/OFF は維持 (既に ON にしている人をリセット時に OFF にしたくない)
      enabled: local.spinEffects?.enabled ?? false,
    };
  }

  // 特殊演出セクション: 演出選択 / 演出確認 のモード切替
  let effectMode = $state<'select' | 'preview'>('select');

  // 大項目ごとの展開状態 (bind:open で個別制御 + 全展開/全折り畳み一括制御用)
  let opOpen = $state(true);
  let soundOpen = $state(true);
  let displayOpen = $state(true);
  let effectOpen = $state(false);

  function expandAll(): void {
    opOpen = true;
    soundOpen = true;
    displayOpen = true;
    effectOpen = true;
  }

  function collapseAll(): void {
    opOpen = false;
    soundOpen = false;
    displayOpen = false;
    effectOpen = false;
  }

  function previewSpin(key: SpinSoundKey): void {
    soundManager.setVolume(appState.preferences.volume);
    soundManager.preview(key);
  }
  function previewWin(key: WinSoundKey): void {
    soundManager.setVolume(appState.preferences.volume);
    soundManager.preview(key);
  }

  function save(): void {
    onsave({ ...local });
    onclose();
  }

  function resetToDefaults(): void {
    if (!confirm('設定を初期値に戻しますか？')) return;
    // randomEachSpin だけは項目リスト側で操作するため現在値を保持する
    const keep = { randomEachSpin: local.randomEachSpin };
    local = { ...defaultSettings(), ...keep };
  }

</script>

<BottomSheet {open} title="設定" {onclose} fadeOut={transientState.effectPreviewActive}>
  <!-- 一括展開/折り畳み -->
  <div class="expand-row">
    <button type="button" class="expand-btn" onclick={expandAll} title="すべて展開">▼ 全展開</button>
    <button type="button" class="expand-btn" onclick={collapseAll} title="すべて折り畳み">▶ 全折り畳み</button>
  </div>

  <!-- 操作 -->
  <details class="group" bind:open={opOpen}>
    <summary class="group-title">操作</summary>

    <!-- プリセットプロファイル -->
    <div class="field">
      <div class="field-label" id="preset-profile-label">プリセットプロファイル</div>
      <div class="seg-group" class:disabled={local.spinPresetRandom} role="radiogroup" aria-labelledby="preset-profile-label">
        {#each PRESET_PROFILES as p (p.key)}
          <button
            type="button"
            class:active={(local.spinPresetProfile ?? 'z') === p.key}
            onclick={() => (local.spinPresetProfile = p.key)}
            role="radio"
            aria-checked={(local.spinPresetProfile ?? 'z') === p.key}
            disabled={local.spinPresetRandom}
          >
            {p.label}
          </button>
        {/each}
      </div>
      <label class="rand-row">
        <input type="checkbox" bind:checked={local.spinPresetRandom} />
        <span>毎スピンでプロファイルをランダム抽選 (X / Y / Z)</span>
      </label>
      <p class="field-hint">
        {local.spinPresetRandom
          ? 'スピン毎に X / Y / Z から均等抽選'
          : (PRESET_PROFILES.find(p => p.key === (local.spinPresetProfile ?? 'z'))?.desc ?? '')}
      </p>
    </div>

    <div class="field">
      <label class="field-label" for="dur">
        スピン時間: {local.spinDurationRandom ? '(ランダム抽選)' : `${(local.spinDurationMs / 1000).toFixed(1)} 秒`}
      </label>
      <div class="preset-row" class:disabled={local.spinDurationRandom} role="group" aria-label="スピン時間プリセット">
        {#each [1000, 3000, 5000, 7000, 9000, 12000] as p (p)}
          <button
            type="button"
            class="preset-btn"
            class:active={local.spinDurationMs === p}
            onclick={() => (local.spinDurationMs = p)}
            aria-pressed={local.spinDurationMs === p}
            disabled={local.spinDurationRandom}
          >
            {p / 1000}秒
          </button>
        {/each}
      </div>
      <input
        id="dur"
        type="range"
        min="1000"
        max="15000"
        step="100"
        bind:value={local.spinDurationMs}
        disabled={local.spinDurationRandom}
      />
      <label class="rand-row">
        <input type="checkbox" bind:checked={local.spinDurationRandom} />
        <span>毎スピンで秒数をランダム抽選 (1/3/5/7/9/12 秒)</span>
      </label>
    </div>

    <div class="field">
      <label class="field-label" for="dur-rand">
        終了時間ランダム化: ±{Math.round((local.spinDurationRandomRatio ?? 0) * 100)}%
        ({((local.spinDurationMs ?? 5000) / 1000 * (local.spinDurationRandomRatio ?? 0)).toFixed(1)} 秒)
      </label>
      <input
        id="dur-rand"
        type="range"
        min="0"
        max={SPIN_DURATION_RANDOM_MAX_RATIO}
        step="0.05"
        bind:value={local.spinDurationRandomRatio}
      />
      <p class="field-hint">
        スピン秒数に対する割合。0% = 固定。短時は巡航→着地を短縮 (急停止)、
        長時は着地を延長 (まだ止まらない?)。
      </p>
    </div>

    <div class="field">
      <label class="field-label" for="phase-rand">
        スピン詳細ランダム化: {Math.round((local.spinPhaseRandomize ?? 0) * 100)}%
      </label>
      <input
        id="phase-rand"
        type="range"
        min="0"
        max="1"
        step="0.05"
        bind:value={local.spinPhaseRandomize}
      />
      <p class="field-hint">
        0% = 固定 (詳細設定通り)。&gt; 0 でスピン毎に 4 段階の終了時間と速度を ±この割合 で
        ランダム揺らし、毎回違う動きに。総時間は維持。
      </p>
    </div>

    <!-- スピン詳細 (4 段階個別調整) — 各段階で時間・速度・曲線を独立指定 -->
    <details class="detail-section">
      <summary class="detail-summary">スピン詳細 (4 段階個別調整)</summary>
      <p class="field-hint detail-intro">
        各段階の終了秒・終了速度・曲線タイプを直接指定します。
        「リセット」で個別にプリセット既定値に戻ります。表示中の値は実行時クランプ後の実効値。
      </p>

      <!-- ① 加速 (push) -->
      <div class="phase-block">
        <div class="phase-header">
          <span class="phase-name">① 加速 (push)</span>
          <span class="phase-time">
            0.00〜{pushEndSec.toFixed(2)} 秒
          </span>
        </div>

        <!-- 終了秒 -->
        <div class="phase-row">
          <span class="phase-rowlabel">終了秒</span>
          <input
            type="range"
            min="0.05"
            max={(totalSec - 0.15).toFixed(2)}
            step="0.05"
            value={pushEndSec}
            oninput={(e) => setTimeSecOverride('pushEndMs', +(e.currentTarget as HTMLInputElement).value)}
          />
          <input
            type="number"
            class="phase-num"
            class:overridden={isOverridden('pushEndMs')}
            min="0.05"
            max={(totalSec - 0.15).toFixed(2)}
            step="0.05"
            value={pushEndSec.toFixed(2)}
            oninput={(e) => setNumericFromInput('pushEndMs', String(+(e.currentTarget as HTMLInputElement).value * 1000))}
          />
          <button class="phase-reset" disabled={!isOverridden('pushEndMs')} onclick={() => clearOverride('pushEndMs')}>×</button>
        </div>

        <!-- 終了速度 (= v1) -->
        <div class="phase-row">
          <span class="phase-rowlabel">終了 RPS</span>
          <input
            type="range"
            min={PEAK_SPEED_MIN_RPS}
            max={PEAK_SPEED_MAX_RPS}
            step="0.5"
            value={effTimes.v1}
            oninput={(e) => setNumericOverride('pushEndRps', +(e.currentTarget as HTMLInputElement).value)}
          />
          <input
            type="number"
            class="phase-num"
            class:overridden={isOverridden('pushEndRps')}
            min={PEAK_SPEED_MIN_RPS}
            max={PEAK_SPEED_MAX_RPS}
            step="0.1"
            value={effTimes.v1.toFixed(1)}
            oninput={(e) => setNumericFromInput('pushEndRps', (e.currentTarget as HTMLInputElement).value)}
          />
          <button class="phase-reset" disabled={!isOverridden('pushEndRps')} onclick={() => clearOverride('pushEndRps')}>×</button>
        </div>

        <!-- 曲線 -->
        <div class="phase-row">
          <span class="phase-rowlabel">曲線</span>
          <div class="curve-group">
            {#each CURVES as c (c.key)}
              <button
                type="button"
                class:active={effTimes.pushCurve === c.key}
                onclick={() => setCurveOverride('pushCurve', c.key)}
              >{c.label}</button>
            {/each}
          </div>
          <button class="phase-reset" disabled={!isOverridden('pushCurve')} onclick={() => clearOverride('pushCurve')}>×</button>
        </div>

        <p class="field-hint phase-summary">
          0.0 → {effTimes.v1.toFixed(1)} RPS, 曲線: {CURVES.find(c => c.key === effTimes.pushCurve)?.label}
        </p>
      </div>

      <!-- ② 巡航 (cruise) -->
      <div class="phase-block">
        <div class="phase-header">
          <span class="phase-name">② 巡航 (cruise)</span>
          <span class="phase-time">{pushEndSec.toFixed(2)}〜{cruiseEndSec.toFixed(2)} 秒</span>
        </div>

        <div class="phase-row">
          <span class="phase-rowlabel">終了秒</span>
          <input
            type="range"
            min={(pushEndSec + 0.05).toFixed(2)}
            max={(totalSec - 0.1).toFixed(2)}
            step="0.05"
            value={cruiseEndSec}
            oninput={(e) => setTimeSecOverride('cruiseEndMs', +(e.currentTarget as HTMLInputElement).value)}
          />
          <input
            type="number"
            class="phase-num"
            class:overridden={isOverridden('cruiseEndMs')}
            min={(pushEndSec + 0.05).toFixed(2)}
            max={(totalSec - 0.1).toFixed(2)}
            step="0.05"
            value={cruiseEndSec.toFixed(2)}
            oninput={(e) => setNumericFromInput('cruiseEndMs', String(+(e.currentTarget as HTMLInputElement).value * 1000))}
          />
          <button class="phase-reset" disabled={!isOverridden('cruiseEndMs')} onclick={() => clearOverride('cruiseEndMs')}>×</button>
        </div>

        <div class="phase-row">
          <span class="phase-rowlabel">終了 RPS</span>
          <input
            type="range"
            min={PEAK_SPEED_MIN_RPS}
            max={PEAK_SPEED_MAX_RPS}
            step="0.5"
            value={effTimes.v2}
            oninput={(e) => setNumericOverride('cruiseEndRps', +(e.currentTarget as HTMLInputElement).value)}
          />
          <input
            type="number"
            class="phase-num"
            class:overridden={isOverridden('cruiseEndRps')}
            min={PEAK_SPEED_MIN_RPS}
            max={PEAK_SPEED_MAX_RPS}
            step="0.1"
            value={effTimes.v2.toFixed(1)}
            oninput={(e) => setNumericFromInput('cruiseEndRps', (e.currentTarget as HTMLInputElement).value)}
          />
          <button class="phase-reset" disabled={!isOverridden('cruiseEndRps')} onclick={() => clearOverride('cruiseEndRps')}>×</button>
        </div>

        <div class="phase-row">
          <span class="phase-rowlabel">曲線</span>
          <div class="curve-group">
            {#each CURVES as c (c.key)}
              <button type="button"
                class:active={effTimes.cruiseCurve === c.key}
                onclick={() => setCurveOverride('cruiseCurve', c.key)}
              >{c.label}</button>
            {/each}
          </div>
          <button class="phase-reset" disabled={!isOverridden('cruiseCurve')} onclick={() => clearOverride('cruiseCurve')}>×</button>
        </div>

        <p class="field-hint phase-summary">
          {effTimes.v1.toFixed(1)} → {effTimes.v2.toFixed(1)} RPS, 曲線: {CURVES.find(c => c.key === effTimes.cruiseCurve)?.label}
          {#if Math.abs(effTimes.v1 - effTimes.v2) < 0.05}(定速){/if}
        </p>
      </div>

      <!-- ③ 減速 (decel) -->
      <div class="phase-block">
        <div class="phase-header">
          <span class="phase-name">③ 減速 (decel)</span>
          <span class="phase-time">{cruiseEndSec.toFixed(2)}〜{decelEndSec.toFixed(2)} 秒</span>
        </div>

        <div class="phase-row">
          <span class="phase-rowlabel">終了秒</span>
          <input
            type="range"
            min={(cruiseEndSec + 0.05).toFixed(2)}
            max={(totalSec - 0.05).toFixed(2)}
            step="0.05"
            value={decelEndSec}
            oninput={(e) => setTimeSecOverride('decelEndMs', +(e.currentTarget as HTMLInputElement).value)}
          />
          <input
            type="number"
            class="phase-num"
            class:overridden={isOverridden('decelEndMs')}
            min={(cruiseEndSec + 0.05).toFixed(2)}
            max={(totalSec - 0.05).toFixed(2)}
            step="0.05"
            value={decelEndSec.toFixed(2)}
            oninput={(e) => setNumericFromInput('decelEndMs', String(+(e.currentTarget as HTMLInputElement).value * 1000))}
          />
          <button class="phase-reset" disabled={!isOverridden('decelEndMs')} onclick={() => clearOverride('decelEndMs')}>×</button>
        </div>

        <div class="phase-row">
          <span class="phase-rowlabel">終了 RPS</span>
          <input
            type="range"
            min={PEAK_SPEED_MIN_RPS}
            max={PEAK_SPEED_MAX_RPS}
            step="0.1"
            value={effTimes.v3}
            oninput={(e) => setNumericOverride('decelEndRps', +(e.currentTarget as HTMLInputElement).value)}
          />
          <input
            type="number"
            class="phase-num"
            class:overridden={isOverridden('decelEndRps')}
            min={PEAK_SPEED_MIN_RPS}
            max={PEAK_SPEED_MAX_RPS}
            step="0.1"
            value={effTimes.v3.toFixed(1)}
            oninput={(e) => setNumericFromInput('decelEndRps', (e.currentTarget as HTMLInputElement).value)}
          />
          <button class="phase-reset" disabled={!isOverridden('decelEndRps')} onclick={() => clearOverride('decelEndRps')}>×</button>
        </div>

        <div class="phase-row">
          <span class="phase-rowlabel">曲線</span>
          <div class="curve-group">
            {#each CURVES as c (c.key)}
              <button type="button"
                class:active={effTimes.decelCurve === c.key}
                onclick={() => setCurveOverride('decelCurve', c.key)}
              >{c.label}</button>
            {/each}
          </div>
          <button class="phase-reset" disabled={!isOverridden('decelCurve')} onclick={() => clearOverride('decelCurve')}>×</button>
        </div>

        <p class="field-hint phase-summary">
          {effTimes.v2.toFixed(1)} → {effTimes.v3.toFixed(1)} RPS, 曲線: {CURVES.find(c => c.key === effTimes.decelCurve)?.label}
        </p>
      </div>

      <!-- ④ 着地 (landing) -->
      <div class="phase-block">
        <div class="phase-header">
          <span class="phase-name">④ 着地 (landing)</span>
          <span class="phase-time">{decelEndSec.toFixed(2)}〜{totalSec.toFixed(2)} 秒 (終端固定)</span>
        </div>

        <div class="phase-row">
          <span class="phase-rowlabel">曲線</span>
          <div class="curve-group">
            {#each CURVES as c (c.key)}
              <button type="button"
                class:active={effTimes.landingCurve === c.key}
                onclick={() => setCurveOverride('landingCurve', c.key)}
              >{c.label}</button>
            {/each}
          </div>
          <button class="phase-reset" disabled={!isOverridden('landingCurve')} onclick={() => clearOverride('landingCurve')}>×</button>
        </div>

        <p class="field-hint phase-summary">
          {effTimes.v3.toFixed(1)} → 0 RPS, 曲線: {CURVES.find(c => c.key === effTimes.landingCurve)?.label}
        </p>
      </div>

      <p class="field-hint">
        曲線: 線形 = 直線 / 減速 = 急→緩 / 加速 = 緩→急 / S字 = 両端緩・中央急
      </p>

      <!-- スピン詳細セクション内の Save / Cancel / Reset -->
      <div class="detail-actions">
        <button
          type="button"
          class="detail-action save"
          disabled={!detailDirty}
          onclick={saveDetailNow}
        >
          保存
        </button>
        <button
          type="button"
          class="detail-action cancel"
          disabled={!detailDirty}
          onclick={cancelDetail}
        >
          キャンセル
        </button>
        <button
          type="button"
          class="detail-action reset"
          onclick={() => {
            if (confirm('スピン詳細をプリセット既定値に戻しますか?')) resetDetailToPreset();
          }}
        >
          初期化
        </button>
      </div>
      <p class="field-hint">
        「保存」: 即座に反映してそのスピン中もテスト可能 / 「キャンセル」: 前回保存時に戻す /
        「初期化」: 全てプリセット既定値に戻して保存
      </p>
    </details>

    <div class="field">
      <div class="field-label" id="spin-dir-label">回転方向</div>
      <div class="seg-group" role="radiogroup" aria-labelledby="spin-dir-label">
        {#each DIRECTIONS as o (o.key)}
          <button
            class:active={local.spinDirection === o.key}
            onclick={() => (local.spinDirection = o.key)}
            role="radio"
            aria-checked={local.spinDirection === o.key}
          >
            {o.label}
          </button>
        {/each}
      </div>
    </div>
  </details>

  <!-- サウンド -->
  <details class="group" bind:open={soundOpen}>
    <summary class="group-title">サウンド</summary>

    <div class="field">
      <div class="field-label" id="spin-sound-label">スピン音</div>
      <div class="sound-list" role="radiogroup" aria-labelledby="spin-sound-label">
        {#each SPIN_SOUNDS as s (s.key)}
          <div class="sound-row">
            <button
              class="sound-pick"
              class:active={local.spinSound === s.key}
              onclick={() => (local.spinSound = s.key)}
              role="radio"
              aria-checked={local.spinSound === s.key}
            >
              <span class="check">{local.spinSound === s.key ? '●' : '○'}</span>
              <span>{s.label}</span>
            </button>
            {#if s.key !== 'mute'}
              <button
                class="preview-btn"
                onclick={() => previewSpin(s.key)}
                aria-label="試聴"
              >
                ▶
              </button>
            {/if}
          </div>
        {/each}
      </div>
    </div>

    <div class="field">
      <div class="field-label" id="win-sound-label">決定音</div>
      <div class="sound-list" role="radiogroup" aria-labelledby="win-sound-label">
        {#each WIN_SOUNDS as s (s.key)}
          <div class="sound-row">
            <button
              class="sound-pick"
              class:active={local.decideSound === s.key}
              onclick={() => (local.decideSound = s.key)}
              role="radio"
              aria-checked={local.decideSound === s.key}
            >
              <span class="check">{local.decideSound === s.key ? '●' : '○'}</span>
              <span>{s.label}</span>
            </button>
            {#if s.key !== 'mute'}
              <button
                class="preview-btn"
                onclick={() => previewWin(s.key)}
                aria-label="試聴"
              >
                ▶
              </button>
            {/if}
          </div>
        {/each}
      </div>
    </div>
  </details>

  <!-- 表示 -->
  <details class="group" bind:open={displayOpen}>
    <summary class="group-title">表示</summary>

    <div class="field">
      <div class="field-label" id="text-or-label">文字の向き</div>
      <div class="seg-group" role="radiogroup" aria-labelledby="text-or-label">
        {#each TEXT_ORIENTATIONS as o (o.key)}
          <button
            class:active={local.textOrientation === o.key}
            onclick={() => (local.textOrientation = o.key)}
            role="radio"
            aria-checked={local.textOrientation === o.key}
          >
            {o.label}
          </button>
        {/each}
      </div>
    </div>

    <div class="field">
      <div class="field-label" id="text-sz-label">文字サイズ</div>
      <div class="seg-group" role="radiogroup" aria-labelledby="text-sz-label">
        {#each TEXT_SIZES as o (o.key)}
          <button
            class:active={local.textSizeMode === o.key}
            onclick={() => (local.textSizeMode = o.key)}
            role="radio"
            aria-checked={local.textSizeMode === o.key}
          >
            {o.label}
          </button>
        {/each}
      </div>
      {#if local.textSizeMode === 'fixed'}
        <div style="margin-top: var(--space-sm);">
          <label class="field-label" for="text-sz-fixed">固定サイズ: {local.textFixedSize}px</label>
          <input
            id="text-sz-fixed"
            type="range"
            min="8"
            max="32"
            step="1"
            bind:value={local.textFixedSize}
          />
        </div>
      {/if}
    </div>

    <div class="field">
      <div class="field-label" id="text-align-label">文字寄せ (放射時)</div>
      <div class="seg-group" role="radiogroup" aria-labelledby="text-align-label">
        {#each TEXT_ALIGNS as o (o.key)}
          <button
            class:active={local.textAlign === o.key}
            onclick={() => (local.textAlign = o.key)}
            role="radio"
            aria-checked={local.textAlign === o.key}
          >
            {o.label}
          </button>
        {/each}
      </div>
      <p class="field-hint">外側＝文字右端を外周ぎりぎり (大きく出せる)、中央＝半径方向の中央</p>
    </div>

    <div class="field">
      <div class="field-label" id="marker-label">マーカー位置</div>
      <div class="seg-group" role="radiogroup" aria-labelledby="marker-label">
        {#each MARKERS as o (o.key)}
          <button
            class:active={local.markerPosition === o.key}
            onclick={() => (local.markerPosition = o.key)}
            role="radio"
            aria-checked={local.markerPosition === o.key}
          >
            {o.label}
          </button>
        {/each}
      </div>
    </div>

    <div class="field">
      <div class="field-label" id="marker-style-label">マーカー描画</div>
      <div class="seg-group" role="radiogroup" aria-labelledby="marker-style-label">
        {#each MARKER_STYLES as o (o.key)}
          <button
            class:active={local.markerStyle === o.key}
            onclick={() => (local.markerStyle = o.key)}
            role="radio"
            aria-checked={local.markerStyle === o.key}
          >
            {o.label}
          </button>
        {/each}
      </div>
    </div>

    <div class="field">
      <div class="field-label" id="seg-dir-label">セグメント並び方向</div>
      <div class="seg-group" role="radiogroup" aria-labelledby="seg-dir-label">
        {#each DIRECTIONS as o (o.key)}
          <button
            class:active={local.segmentDirection === o.key}
            onclick={() => (local.segmentDirection = o.key)}
            role="radio"
            aria-checked={local.segmentDirection === o.key}
          >
            {o.label}
          </button>
        {/each}
      </div>
    </div>

  </details>

  <!-- ─── 特殊演出設定 ─── -->
  <details class="group" bind:open={effectOpen}>
    <summary class="group-title">
      特殊演出（テスト版）
      <span class="badge-onoff">{local.spinEffects?.enabled ? 'ON' : 'OFF'}</span>
    </summary>

    <div class="field">
      <label class="toggle-row">
        <span class="field-label" style="margin-bottom:0">演出マスター ON/OFF</span>
        <input type="checkbox"
          checked={local.spinEffects?.enabled ?? false}
          onchange={(e) => { local.spinEffects = { ...local.spinEffects, enabled: (e.target as HTMLInputElement).checked }; }}
        />
      </label>
    </div>

    <div class="reset-row">
      <button type="button" class="reset-btn" onclick={resetEffectsToDefault}>演出設定を初期値に戻す</button>
    </div>

    <!-- モード切替: 演出選択 / 演出確認 -->
    <div class="field">
      <div class="field-label" id="effect-mode-label">モード</div>
      <div class="seg-group" role="radiogroup" aria-labelledby="effect-mode-label">
        <button
          class:active={effectMode === 'select'}
          onclick={() => (effectMode = 'select')}
          role="radio"
          aria-checked={effectMode === 'select'}
        >演出選択</button>
        <button
          class:active={effectMode === 'preview'}
          onclick={() => (effectMode = 'preview')}
          role="radio"
          aria-checked={effectMode === 'preview'}
        >演出確認</button>
      </div>
      <p class="field-hint">
        {effectMode === 'select'
          ? '各演出のバリエーション・確率・タイミングを設定します。'
          : '項目をタップすると、選択中のバリエーションでその場で試聴/視聴します。'}
      </p>
    </div>

    {#each EFFECT_GROUPS as grp}
      <div class="effect-group-label">
        <span>{grp.label}</span>
      </div>
      {#each grp.keys as key}
        {@const cfg = local.spinEffects?.effects?.[key]}
        {#if cfg}
          {#if effectMode === 'select'}
            <!-- ──── 演出選択モード ──── -->
            <div class="effect-row">
              <label class="effect-name">
                <input type="checkbox"
                  checked={cfg.enabled}
                  onchange={(e) => {
                    local.spinEffects.effects[key] = { ...cfg, enabled: (e.target as HTMLInputElement).checked };
                  }}
                />
                {EFFECT_LABELS[key]}
              </label>

              <!-- バリエーション 6 択ラジオ: 1〜5 + R(ランダム) -->
              <div class="variant-radio" role="radiogroup" aria-label="バリエーション選択">
                {#each [1, 2, 3, 4, 5] as v (v)}
                  <button type="button" class="variant-radio-btn"
                    class:active={cfg.selectedVariant === v}
                    disabled={!cfg.enabled}
                    onclick={() => { local.spinEffects.effects[key] = { ...cfg, selectedVariant: v }; }}
                    role="radio" aria-checked={cfg.selectedVariant === v}
                    title="バリエーション {v}">{v}</button>
                {/each}
                <button type="button" class="variant-radio-btn variant-radio-random"
                  class:active={cfg.selectedVariant === 0}
                  disabled={!cfg.enabled}
                  onclick={() => { local.spinEffects.effects[key] = { ...cfg, selectedVariant: 0 }; }}
                  role="radio" aria-checked={cfg.selectedVariant === 0}
                  aria-label="ランダム"
                  title="毎スピン 1〜5 からランダム抽選">🎲</button>
              </div>

              <!-- 確率範囲スライダー + ランダムトグル -->
              <div class="range-block" class:disabled={!cfg.enabled}>
                <div class="range-label">
                  <span>確率: {cfg.probabilityRandom
                    ? '0〜100% (完全ランダム)'
                    : `${Math.round(cfg.probabilityRange.min * 100)}% 〜 ${Math.round(cfg.probabilityRange.max * 100)}%`
                  }</span>
                  <button type="button" class="random-toggle"
                    class:active={cfg.probabilityRandom}
                    disabled={!cfg.enabled}
                    onclick={() => { local.spinEffects.effects[key] = { ...cfg, probabilityRandom: !cfg.probabilityRandom }; }}
                    title="完全ランダム (毎スピン 0〜100% から抽選)"
                    aria-pressed={cfg.probabilityRandom}
                    aria-label="確率をランダム化">🎲</button>
                </div>
                <div class="range-sliders" class:slider-off={cfg.probabilityRandom}>
                  <input type="range" min="0" max="1" step="0.01"
                    value={cfg.probabilityRange.min}
                    disabled={!cfg.enabled || cfg.probabilityRandom}
                    aria-label="確率 最小"
                    oninput={(e) => {
                      const v = parseFloat((e.target as HTMLInputElement).value);
                      const newMin = Math.min(v, cfg.probabilityRange.max);
                      local.spinEffects.effects[key] = { ...cfg, probabilityRange: { min: newMin, max: cfg.probabilityRange.max } };
                    }}
                  />
                  <input type="range" min="0" max="1" step="0.01"
                    value={cfg.probabilityRange.max}
                    disabled={!cfg.enabled || cfg.probabilityRandom}
                    aria-label="確率 最大"
                    oninput={(e) => {
                      const v = parseFloat((e.target as HTMLInputElement).value);
                      const newMax = Math.max(v, cfg.probabilityRange.min);
                      local.spinEffects.effects[key] = { ...cfg, probabilityRange: { min: cfg.probabilityRange.min, max: newMax } };
                    }}
                  />
                </div>
              </div>

              <!-- タイミング範囲スライダー + ランダムトグル -->
              <div class="range-block" class:disabled={!cfg.enabled}>
                <div class="range-label">
                  <span>タイミング: {cfg.timingRandom
                    ? '0〜100% (完全ランダム)'
                    : `${Math.round(cfg.timingRange.min * 100)}% 〜 ${Math.round(cfg.timingRange.max * 100)}%`
                  }</span>
                  <button type="button" class="random-toggle"
                    class:active={cfg.timingRandom}
                    disabled={!cfg.enabled}
                    onclick={() => { local.spinEffects.effects[key] = { ...cfg, timingRandom: !cfg.timingRandom }; }}
                    title="完全ランダム (毎スピン 0〜100% から抽選)"
                    aria-pressed={cfg.timingRandom}
                    aria-label="タイミングをランダム化">🎲</button>
                </div>
                <div class="range-sliders" class:slider-off={cfg.timingRandom}>
                  <input type="range" min="0" max="1" step="0.01"
                    value={cfg.timingRange.min}
                    disabled={!cfg.enabled || cfg.timingRandom}
                    aria-label="タイミング 最小"
                    oninput={(e) => {
                      const v = parseFloat((e.target as HTMLInputElement).value);
                      const newMin = Math.min(v, cfg.timingRange.max);
                      local.spinEffects.effects[key] = { ...cfg, timingRange: { min: newMin, max: cfg.timingRange.max } };
                    }}
                  />
                  <input type="range" min="0" max="1" step="0.01"
                    value={cfg.timingRange.max}
                    disabled={!cfg.enabled || cfg.timingRandom}
                    aria-label="タイミング 最大"
                    oninput={(e) => {
                      const v = parseFloat((e.target as HTMLInputElement).value);
                      const newMax = Math.max(v, cfg.timingRange.min);
                      local.spinEffects.effects[key] = { ...cfg, timingRange: { min: cfg.timingRange.min, max: newMax } };
                    }}
                  />
                </div>
              </div>
            </div>
          {:else}
            <!-- ──── 演出確認モード ──── 1〜5 + 🎲 のボタンを並べる、各タップで試聴 -->
            <div class="confirm-row">
              <span class="confirm-name">{EFFECT_LABELS[key]}</span>
              <div class="confirm-buttons">
                {#each [1, 2, 3, 4, 5] as v (v)}
                  <button type="button" class="confirm-btn"
                    onclick={() => previewEffect(appState.activeRouletteId, key, v)}
                    title="バリエーション {v} を試聴">{v}</button>
                {/each}
                <button type="button" class="confirm-btn confirm-random"
                  onclick={() => previewEffect(appState.activeRouletteId, key, 0)}
                  aria-label="ランダム試聴"
                  title="1〜5 からランダムに試聴">🎲</button>
              </div>
            </div>
          {/if}
        {/if}
      {/each}
    {/each}
  </details>

  <div class="reset-row">
    <button class="reset-btn" onclick={resetToDefaults}>初期値に戻す</button>
  </div>

  <div class="actions">
    <button class="cancel" onclick={onclose}>キャンセル</button>
    <button class="save" onclick={save}>保存</button>
  </div>

  <div class="build-info">
    <div>{__APP_VERSION__}</div>
  </div>
</BottomSheet>

<style>
  .group {
    margin-bottom: var(--space-xl);
  }

  .group-title {
    margin: 0 0 var(--space-sm);
    padding: 6px 0;
    border-bottom: 1px solid var(--color-border);
    font-size: var(--font-size-sm);
    font-weight: 700;
    color: var(--color-accent);
    letter-spacing: 0.04em;
    cursor: pointer;
    user-select: none;
    list-style: none;
    position: relative;
    padding-left: 18px;
  }

  /* 折り畳み三角アイコン */
  .group-title::before {
    content: '▼';
    position: absolute;
    left: 0;
    font-size: 10px;
    transition: transform 0.15s;
  }

  .group:not([open]) > .group-title::before {
    transform: rotate(-90deg);
  }

  /* デフォルト三角を隠す (Webkit) */
  .group-title::-webkit-details-marker {
    display: none;
  }

  .rand-row {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: var(--space-sm);
    font-size: 11px;
    color: var(--color-fg-muted);
    cursor: pointer;
  }

  .rand-row input[type='checkbox'] {
    width: 16px;
    height: 16px;
    accent-color: var(--color-accent);
    margin: 0;
  }

  .seg-group.disabled,
  .preset-row.disabled {
    opacity: 0.4;
    pointer-events: none;
  }

  .field {
    margin-bottom: var(--space-md);
  }

  .field-label {
    display: block;
    font-size: var(--font-size-sm);
    font-weight: 600;
    color: var(--color-fg-muted);
    margin-bottom: var(--space-sm);
  }

  .field-hint {
    margin: 6px 0 0;
    font-size: 11px;
    color: var(--color-fg-muted);
    line-height: 1.4;
  }

  input[type='range'] {
    width: 100%;
    accent-color: var(--color-accent);
  }

  .preset-row {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 4px;
    margin-bottom: 8px;
  }

  .detail-section {
    margin-top: var(--space-md);
    padding: var(--space-sm);
    background: var(--color-bg);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-sm);
  }

  .detail-summary {
    cursor: pointer;
    font-weight: 600;
    color: var(--color-fg);
    padding: 4px 0;
    user-select: none;
  }

  .detail-intro {
    margin-top: 6px;
  }

  .phase-block {
    margin-top: var(--space-md);
    padding: var(--space-sm);
    background: var(--color-bg);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-sm);
  }

  .phase-header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 6px;
    border-bottom: 1px dashed var(--color-border);
    padding-bottom: 4px;
  }

  .phase-name {
    font-weight: 700;
    font-size: var(--font-size-sm);
    color: var(--color-fg);
  }

  .phase-time {
    font-size: 11px;
    color: var(--color-fg-muted);
    font-variant-numeric: tabular-nums;
  }

  .phase-row {
    display: grid;
    grid-template-columns: 70px 1fr 60px 28px;
    align-items: center;
    gap: 6px;
    margin-bottom: 4px;
  }

  .phase-rowlabel {
    font-size: 11px;
    color: var(--color-fg-muted);
    font-weight: 600;
  }

  .phase-reset {
    width: 24px;
    height: 24px;
    padding: 0;
    border-radius: 50%;
    background: var(--color-border);
    color: var(--color-fg);
    font-size: 12px;
    line-height: 1;
  }

  .phase-reset:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  .curve-group {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 3px;
    background: var(--color-border);
    padding: 2px;
    border-radius: 4px;
  }

  .curve-group button {
    padding: 4px 0;
    font-size: 10px;
    font-weight: 600;
    border-radius: 3px;
    background: var(--color-bg);
    color: var(--color-fg-muted);
  }

  .curve-group button.active {
    background: var(--color-accent);
    color: var(--color-accent-fg);
  }

  .phase-summary {
    margin-top: 4px;
    font-size: 10px;
    color: var(--color-fg-muted);
    font-style: italic;
  }

  .phase-num {
    width: 56px;
    padding: 4px 6px;
    font-size: 11px;
    font-weight: 600;
    text-align: right;
    background: var(--color-bg);
    color: var(--color-fg);
    border: 1px solid var(--color-border);
    border-radius: 4px;
    font-variant-numeric: tabular-nums;
  }

  .phase-num.overridden {
    color: var(--color-accent);
    border-color: var(--color-accent);
  }

  .detail-actions {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;
    margin-top: var(--space-md);
  }

  .detail-action {
    padding: 10px 0;
    font-size: var(--font-size-sm);
    font-weight: 700;
    border-radius: var(--radius-sm);
  }

  .detail-action.save {
    background: var(--color-accent);
    color: var(--color-accent-fg);
  }

  .detail-action.cancel {
    background: var(--color-border);
    color: var(--color-fg);
  }

  .detail-action.reset {
    background: transparent;
    color: var(--color-fg-muted);
    border: 1px solid var(--color-border);
  }

  .detail-action:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .preset-btn {
    padding: 8px 0;
    font-size: var(--font-size-sm);
    font-weight: 600;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-sm);
    background: var(--color-bg);
    color: var(--color-fg-muted);
  }

  .preset-btn.active {
    background: var(--color-accent);
    color: var(--color-accent-fg);
    border-color: var(--color-accent);
  }

  .seg-group {
    display: flex;
    gap: 3px;
    background: var(--color-border);
    padding: 3px;
    border-radius: var(--radius-sm);
  }

  .seg-group button {
    flex: 1;
    padding: 8px 4px;
    font-size: var(--font-size-sm);
    font-weight: 500;
    border-radius: 6px;
    color: var(--color-fg-muted);
  }

  .seg-group button.active {
    background: var(--color-bg);
    color: var(--color-fg);
    box-shadow: var(--shadow-sm);
    font-weight: 700;
  }

  .sound-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .sound-row {
    display: flex;
    align-items: center;
    gap: var(--space-sm);
    padding: 4px;
    background: var(--color-bg);
    border-radius: var(--radius-sm);
  }

  .sound-pick {
    flex: 1;
    display: flex;
    align-items: center;
    gap: var(--space-sm);
    padding: 10px var(--space-sm);
    text-align: left;
    border-radius: 6px;
    color: var(--color-fg);
    font-size: var(--font-size-base);
  }

  .sound-pick.active {
    background: rgba(233, 69, 96, 0.1);
    font-weight: 600;
  }

  .check {
    color: var(--color-accent);
    width: 16px;
    text-align: center;
    flex-shrink: 0;
  }

  .preview-btn {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--color-border);
    border-radius: var(--radius-sm);
    font-size: 12px;
    color: var(--color-fg);
    flex-shrink: 0;
  }

  .preview-btn:active {
    background: var(--color-accent);
    color: var(--color-accent-fg);
  }

  .reset-row {
    margin-top: var(--space-md);
    text-align: center;
  }

  .reset-btn {
    font-size: var(--font-size-sm);
    color: var(--color-fg-muted);
    padding: 8px 16px;
    border-radius: var(--radius-sm);
    border: 1px solid var(--color-border);
  }

  .reset-btn:active {
    background: var(--color-border);
  }

  .actions {
    display: flex;
    gap: var(--space-sm);
    margin-top: var(--space-md);
  }

  .actions button {
    flex: 1;
    padding: 12px;
    border-radius: var(--radius-sm);
    font-weight: 600;
    font-size: var(--font-size-base);
  }

  .cancel {
    background: var(--color-border);
    color: var(--color-fg);
  }

  .save {
    background: var(--color-accent);
    color: var(--color-accent-fg);
  }

  .build-info {
    margin-top: var(--space-lg);
    padding-top: var(--space-md);
    border-top: 1px solid var(--color-border);
    font-size: 11px;
    color: var(--color-fg-muted);
    line-height: 1.6;
    font-family: ui-monospace, 'SFMono-Regular', monospace;
  }

  /* ─── 特殊演出設定 ─── */

  .badge-onoff {
    font-size: 11px;
    font-weight: 700;
    margin-left: auto;
    padding: 2px 7px;
    border-radius: 10px;
    background: var(--color-border);
    color: var(--color-fg-muted);
  }

  .toggle-row {
    display: flex;
    align-items: center;
    gap: var(--space-md);
    cursor: pointer;
  }

  .toggle-row input[type='checkbox'] {
    width: 20px;
    height: 20px;
    accent-color: var(--color-accent);
    flex-shrink: 0;
    margin-left: auto;
  }

  .effect-group-label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-sm);
    font-size: 11px;
    font-weight: 700;
    color: var(--color-fg-muted);
    margin: var(--space-md) 0 var(--space-sm);
  }
  .effect-group-label > span:first-child {
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
  .effect-row {
    border: 1px solid var(--color-border);
    border-radius: var(--radius-sm);
    padding: var(--space-sm) var(--space-md);
    margin-bottom: var(--space-sm);
  }

  .effect-name {
    display: flex;
    align-items: center;
    gap: var(--space-sm);
    font-size: var(--font-size-sm);
    font-weight: 600;
    cursor: pointer;
    user-select: none;
    flex: 1;
  }

  /* ──── バリエーション 6 択ラジオ (1〜5 + R) ──── */
  .variant-radio {
    display: flex;
    gap: 4px;
    margin-top: var(--space-sm);
    flex-wrap: wrap;
  }

  .variant-radio-btn {
    flex: 1;
    min-width: 32px;
    height: 32px;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-sm);
    background: var(--color-bg);
    color: var(--color-fg-muted);
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    transition: background 0.15s, color 0.15s, border-color 0.15s;
  }

  .variant-radio-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .variant-radio-btn.active {
    background: var(--color-accent);
    color: var(--color-accent-fg);
    border-color: var(--color-accent);
  }

  .variant-radio-btn.variant-radio-random {
    color: var(--color-fg-muted);
  }
  .variant-radio-btn.variant-radio-random.active {
    background: var(--color-fg-muted);
    color: var(--color-bg);
    border-color: var(--color-fg-muted);
  }

  /* ──── 範囲スライダー (2 thumb): min と max を上下に並べる ──── */
  .range-block {
    margin-top: var(--space-sm);
  }

  .range-block.disabled {
    opacity: 0.4;
    pointer-events: none;
  }

  .range-label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-sm);
    font-size: 11px;
    color: var(--color-fg-muted);
    margin-bottom: 4px;
  }

  /* ランダムトグル (確率/タイミングを完全ランダム化) */
  .random-toggle {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: var(--color-border);
    color: var(--color-fg-muted);
    font-size: 13px;
    cursor: pointer;
    border: 1px solid var(--color-border);
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s, color 0.15s, border-color 0.15s;
  }

  .random-toggle.active {
    background: var(--color-fg-muted);
    color: var(--color-bg);
    border-color: var(--color-fg-muted);
  }

  .random-toggle:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .range-sliders {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .range-sliders.slider-off {
    opacity: 0.4;
  }

  .range-sliders input[type='range'] {
    width: 100%;
    accent-color: var(--color-accent);
    /* touch-action: pan-y で「縦スクロール優先・横ドラッグでスライダー操作」を実現。
     * これによりシート内の縦スクロール中にスライダーを誤操作しても値が変わらない。 */
    touch-action: pan-y;
  }

  /* ──── 演出確認モード: 演出名 + 1〜5 + 🎲 ボタン ──── */
  .confirm-row {
    display: flex;
    align-items: center;
    gap: var(--space-sm);
    padding: 8px var(--space-md);
    margin-bottom: var(--space-sm);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-sm);
    background: var(--color-bg);
  }

  .confirm-name {
    flex: 1;
    font-size: var(--font-size-sm);
    font-weight: 600;
    color: var(--color-fg);
  }

  .confirm-buttons {
    display: flex;
    gap: 4px;
    flex-shrink: 0;
  }

  .confirm-btn {
    width: 30px;
    height: 30px;
    border-radius: var(--radius-sm);
    background: var(--color-accent);
    color: var(--color-accent-fg);
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    border: 1px solid var(--color-accent);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .confirm-btn:active {
    transform: scale(0.92);
  }

  .confirm-btn.confirm-random {
    background: var(--color-fg-muted);
    color: var(--color-bg);
    border-color: var(--color-fg-muted);
  }

  /* 全展開/全折り畳みボタン (設定シート上部) */
  .expand-row {
    display: flex;
    gap: var(--space-sm);
    margin-bottom: var(--space-md);
  }

  .expand-btn {
    flex: 1;
    padding: 6px;
    font-size: 12px;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-sm);
    background: var(--color-bg);
    color: var(--color-fg-muted);
    cursor: pointer;
  }

  .expand-btn:active {
    background: var(--color-border);
  }

  .effect-name input[type='checkbox'] {
    width: 16px;
    height: 16px;
    accent-color: var(--color-accent);
    flex-shrink: 0;
  }

</style>
